import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import cn from 'classnames'
import { ArrowLeft, ChevronDown, ChevronRight, Mail } from 'lucide-react'
import useStore from 'state/knovStore'
import usePanelContext from 'refactor/hooks/usePanelContext'
import useOpenRight from 'refactor/hooks/useOpenRight'
import useGetQuest from 'refactor/hooks/api/useGetQuest'
import useGetUser from 'refactor/hooks/api/useGetUser'
import useTeamLookup from 'components/teams/useTeamLookup'
import { getStreamName } from 'components/filters/StreamName'
import answerModel from 'components/answers/answerModel'
import questModel from 'components/quests/questModel'
import { getCachedAnswer, getCachedQuest, getCachedUser } from 'state/cache'
import { resolveQuestParentQuestId } from 'state/questAncestors'
import { isMobile } from 'lib/device'
import {
    directMessageCounterpartId,
    isDirectMessageConversationHead,
    isDirectMessageQuest,
    isDirectMessageRoom,
    resolveDirectMessageConversationTarget,
} from 'lib/directMessages'
import { withDepth } from 'components/filters/useStreamFilters'
import useInsertLeft from 'refactor/hooks/useInsertLeft'
import { slideTo } from 'state/imperativeApis/swiperApi'
import { setManualSlide } from 'components/PanelController/PanelController'
import { v4 as uuid } from 'uuid'
import { resolveBackAction } from 'lib/panelUpTarget'
import { describePanelFilter, resolveUpTarget } from './upTarget'
import { slideToInsertedPanel } from './slideToInsertedPanel'
import AncestorCrumbs from 'components/quests/Quest/AncestorCrumbs'
import { loadQuest } from 'components/quests/Quest/DirectMessageBranchCrumb'
import { ancestorCrumbTarget } from 'lib/ancestorCrumbs'
import type { Filter } from 'components/filters/useStreamFilters'
import type { Quest } from 'types/quests'
import {
    MOBILE_CHROME_PEEK_MS,
    MOBILE_CHROME_RANGE_PX,
    MOBILE_CHROME_SETTLE_DELAY_MS,
    nextChromeOffset,
    setMobileChromeHidden,
    settleChromeOffset,
    writeChromeProgress,
} from 'lib/mobileChromeAutoHide'
import { resolvePlaceBarInsets } from './placeBarInsets'
import { PanelScopeIcon } from './FloatingFilterIcon'
import ViewPill from './ViewPill'
import { usePanelViewControls } from 'lib/panelViewControls'
import UserIcon from 'components/users/UserIcon'
import PermsOptions from 'components/quests/PermsOptions'
import perms from 'lib/perms'
import { canPublishFromQuestBreadcrumb } from 'lib/questBreadcrumb'
import styles from './panel-place-bar.module.scss'

// The bar sits beside its panel's scroll container (PanelContainer renders it
// over the top of the scroll area), not inside it.
function panelScroller(node: Element | null): HTMLElement | null {
    const bar = node?.closest('[data-testid="panel-place-bar"]')
    return (
        bar?.parentElement?.querySelector<HTMLElement>('[data-testid="panel-scroll-container"]') ||
        null
    )
}

// The stream's own header card names the channel at the top, so the bar only
// shows it once that card has scrolled most of the way under the bar. A thread
// shows its breadcrumb from the start and adds the thread's title once the head
// post's author row has gone under the bar (useHeadPostCovered).
const STREAM_HEADER_CLEAR_PX = 140

function useScrolledPast(barRef: React.RefObject<HTMLElement>, threshold: number) {
    const [scrolledPast, setScrolledPast] = useState(false)

    useEffect(() => {
        const scroller = panelScroller(barRef.current)
        if (!scroller) return
        const update = () => setScrolledPast(scroller.scrollTop > threshold)
        update()
        scroller.addEventListener('scroll', update, { passive: true })
        return () => scroller.removeEventListener('scroll', update)
    }, [barRef, threshold])

    return scrolledPast
}

// A thread's title (its head post, with the author's avatar) joins the header
// the moment the head post's author row has scrolled under the bar: measured
// where the head post is, not as a fixed scroll distance, which ancestor rows
// above it and a long post's height both throw off. Until the head post is on
// the page, nothing is covered.
const HEAD_POST_SELECTOR = '[data-answer-role="title"]'
const HEAD_POST_AUTHOR_ROW_SELECTOR = '[class*="userAndControlsContainer"]'

function useHeadPostCovered(barRef: React.RefObject<HTMLElement>, watchKey: unknown) {
    const [covered, setCovered] = useState(false)

    useEffect(() => {
        if (!watchKey) return setCovered(false)
        const scroller = panelScroller(barRef.current)
        if (!scroller) return
        const update = () => {
            const head = scroller.querySelector<HTMLElement>(HEAD_POST_SELECTOR)
            const bar = barRef.current
            if (!head || !bar) return setCovered(false)
            const row = head.querySelector<HTMLElement>(HEAD_POST_AUTHOR_ROW_SELECTOR) || head
            setCovered(row.getBoundingClientRect().bottom <= bar.getBoundingClientRect().bottom)
        }
        // The head post mounts once the thread loads: keep looking for a moment.
        let frame = 0
        const until = performance.now() + SPINE_SETTLE_MS
        const settle = () => {
            update()
            if (performance.now() < until) frame = requestAnimationFrame(settle)
        }
        settle()
        scroller.addEventListener('scroll', update, { passive: true })
        return () => {
            cancelAnimationFrame(frame)
            scroller.removeEventListener('scroll', update)
        }
    }, [barRef, watchKey])

    return covered
}

// A thread's ancestor list (QuestAncestorSpine) already shows the chain of
// posts above it at the top of the thread. The header names only what is off
// screen, so its breadcrumb waits until that list has passed under the bar.
// No list on the page (root thread, or the list is off) counts as covered.
const ANCESTOR_SPINE_SELECTOR = '[data-testid="quest-ancestor-spine"]'
const SPINE_SETTLE_MS = 1500

function useAncestorSpineCovered(barRef: React.RefObject<HTMLElement>, watchKey: unknown) {
    const [covered, setCovered] = useState(true)

    useEffect(() => {
        const scroller = panelScroller(barRef.current)
        if (!scroller) return
        const update = () => {
            const spine = scroller.querySelector<HTMLElement>(ANCESTOR_SPINE_SELECTOR)
            const bar = barRef.current
            if (!spine || !bar) return setCovered(true)
            setCovered(spine.getBoundingClientRect().bottom <= bar.getBoundingClientRect().bottom)
        }
        // The list mounts once the thread loads: keep looking for a moment.
        let frame = 0
        const until = performance.now() + SPINE_SETTLE_MS
        const settle = () => {
            update()
            if (performance.now() < until) frame = requestAnimationFrame(settle)
        }
        settle()
        scroller.addEventListener('scroll', update, { passive: true })
        return () => {
            cancelAnimationFrame(frame)
            scroller.removeEventListener('scroll', update)
        }
    }, [barRef, watchKey])

    return covered
}

// A direct message's conversation opens on its letter header (the hero, marked
// data-direct-message-letter): the bar stays concealed until that letter has
// scrolled under it, as a channel's waits for its header card.
const DIRECT_MESSAGE_LETTER_SELECTOR = '[data-direct-message-letter]'

function useLetterCovered(barRef: React.RefObject<HTMLElement>, enabled: boolean) {
    const [covered, setCovered] = useState(false)

    useEffect(() => {
        if (!enabled) return setCovered(false)
        const scroller = panelScroller(barRef.current)
        if (!scroller) return
        const update = () => {
            const letter = scroller.querySelector<HTMLElement>(DIRECT_MESSAGE_LETTER_SELECTOR)
            const bar = barRef.current
            if (!letter || !bar) return setCovered(false)
            setCovered(letter.getBoundingClientRect().bottom <= bar.getBoundingClientRect().bottom)
        }
        // The letter mounts once the conversation loads: keep looking for a moment.
        let frame = 0
        const until = performance.now() + SPINE_SETTLE_MS
        const settle = () => {
            update()
            if (performance.now() < until) frame = requestAnimationFrame(settle)
        }
        settle()
        scroller.addEventListener('scroll', update, { passive: true })
        return () => {
            cancelAnimationFrame(frame)
            scroller.removeEventListener('scroll', update)
        }
    }, [barRef, enabled])

    return covered
}

// fixed_header, phones: the focused panel's scrolling slides the header and the
// bottom chrome out on the way down and back on the way up, following the
// scroll and settling when it stops (lib/mobileChromeAutoHide). Rides beside
// the bar's own scroll reading rather than adding a listener to the panel
// shell; each scroll event that moves it writes one variable on a handful of
// elements.
function useMobileChromeAutoHide(
    barRef: React.RefObject<HTMLElement>,
    panelIndex: number | undefined,
    headPostCovered: boolean,
) {
    const peekRef = useRef<(() => void) | null>(null)
    // Only the panel on screen drives the chrome. Every phone panel carries a
    // bar; one scrolled off-screen must neither hide nor keep hidden the chrome.
    const isCentre = useStore(state => state.centerIndex === panelIndex)
    const enabled = isMobile && isCentre
    useEffect(() => {
        if (!enabled) return
        const scroller = panelScroller(barRef.current)
        if (!scroller) return
        const panelEl = scroller.closest('.panel-container-comp')
        // Everything that slides with the chrome (each turns the progress into
        // its own distance in CSS).
        const topTargets = () => [
            barRef.current,
            document.querySelector<HTMLElement>('[data-fixed-header="phone"]'),
        ]
        const bottomTargets = () => [
            document.querySelector<HTMLElement>('[data-bottom-controls]'),
            panelEl?.querySelector<HTMLElement>('[data-testid="panel-bottom-editor-host"]'),
            panelEl?.querySelector<HTMLElement>('[data-testid="panel-jump-latest-chip"]'),
            panelEl?.querySelector<HTMLElement>('[data-testid="panel-remove-button"]'),
        ]
        const clampedTop = () =>
            Math.max(0, Math.min(scroller.scrollTop, scroller.scrollHeight - scroller.clientHeight))
        let offset = 0
        let lastTop = clampedTop()
        let idle = 0
        let applied = -1
        // While the header peeks (below), it stays out and only the bottom
        // chrome follows the scroll.
        let peeking = 0
        const apply = (settling: boolean) => {
            const progress = offset / MOBILE_CHROME_RANGE_PX
            if (!settling && progress === applied) return
            applied = progress
            const targets = peeking ? bottomTargets() : [...topTargets(), ...bottomTargets()]
            writeChromeProgress(targets, progress, settling)
            setMobileChromeHidden(progress >= 1)
        }
        // The thread's head post has just gone under the header, which now
        // names the thread: hold the header out (or bring it back in), though
        // the reader is scrolling down, and after a moment let it slide off
        // with the scroll again. Scrolling up meanwhile brings
        // the whole chrome back as usual.
        const endPeek = () => {
            window.clearTimeout(peeking)
            peeking = 0
        }
        peekRef.current = () => {
            if (peeking) return
            writeChromeProgress(topTargets(), 0, true)
            peeking = window.setTimeout(() => {
                endPeek()
                writeChromeProgress(topTargets(), offset / MOBILE_CHROME_RANGE_PX, true)
            }, MOBILE_CHROME_PEEK_MS)
        }
        // A panel arriving on screen starts with the chrome showing, and keeps
        // it until the reader scrolls it themselves: the scrolls a panel makes
        // on its own as it lands (past a thread's ancestors to its head post,
        // back to a saved place, to a target post) would otherwise hide the
        // header before anyone saw where they are.
        apply(true)
        let reading = false
        const startReading = () => {
            reading = true
        }
        const onScroll = () => {
            // Rubber-band overscroll at either end is not the reader's direction.
            const top = clampedTop()
            if (!reading) {
                lastTop = top
                return
            }
            if (peeking && top < lastTop) {
                // Scrolling up ends the peek with the whole chrome showing, as a
                // scroll up brings it back anyway.
                endPeek()
                offset = 0
                lastTop = top
                apply(true)
                return
            }
            offset = nextChromeOffset(offset, top - lastTop, top)
            lastTop = top
            // In the scroll event itself, not a frame later: the chrome moves in
            // the same paint as the content it follows, or it trails a frame
            // behind and wobbles against it.
            apply(false)
            window.clearTimeout(idle)
            idle = window.setTimeout(() => {
                offset = settleChromeOffset(offset)
                apply(true)
            }, MOBILE_CHROME_SETTLE_DELAY_MS)
        }
        scroller.addEventListener('scroll', onScroll, { passive: true })
        // Anywhere on the panel: a drag can start over the bottom editor or the
        // header, outside the scroller, and still scroll it.
        const inputRoot = panelEl || scroller
        inputRoot.addEventListener('touchstart', startReading, { passive: true })
        inputRoot.addEventListener('wheel', startReading, { passive: true })
        return () => {
            scroller.removeEventListener('scroll', onScroll)
            inputRoot.removeEventListener('touchstart', startReading)
            inputRoot.removeEventListener('wheel', startReading)
            window.clearTimeout(idle)
            endPeek()
            peekRef.current = null
            offset = 0
            apply(true)
        }
    }, [barRef, enabled])

    useEffect(() => {
        if (headPostCovered) peekRef.current?.()
    }, [headPostCovered])
}

function useCornerInsets(panelIndex: number | undefined, isThread: boolean) {
    return useStore(state =>
        resolvePlaceBarInsets({
            onePanel: isMobile || state.numPanels === 1,
            // Phones: a thread gives the corners (sidebar button, avatar) to the
            // breadcrumb; PanelController hides them for a centred thread.
            cornersHidden: isMobile && isThread,
            centerIndex: state.centerIndex,
            numPanels: state.numPanels,
            panelIndex,
        }),
    )
}

// A thread's arrow is BACK: it slides to the panel on the left (the same as a
// swipe right, or the desktop's left edge chevron, which steps aside for it).
// Landed on directly, with nothing on the left, it goes UP instead: it opens
// the crumb before the thread there (the parent thread, or the channel for a
// root thread) and slides to it (lib/panelUpTarget). Channels never show it.
function PanelUp({ quest }: { quest: Quest | null }) {
    const { panel } = usePanelContext()
    const insertLeft = useInsertLeft(panel?.panelId)
    const left = useStore(state => {
        const leftPanel = state.panels.state[state.centerIndex - 1]
        return leftPanel && !leftPanel.empty ? leftPanel : null
    })
    const action = resolveBackAction(left, resolveUpTarget(quest))
    if (!action) return null
    const label = action.kind === 'slide' ? describePanelFilter(left?.filter) : action.target.label

    const goBack = () => {
        const { centerIndex } = useStore.getState()
        setManualSlide(true)
        if (action.kind === 'slide') {
            slideTo(centerIndex - 1)
            return
        }
        const panelId = uuid()
        insertLeft({ panelId, filter: action.target.filter as Filter })
        slideToInsertedPanel(panelId, {
            getIndex: id => useStore.getState().panels.getIndexById(id),
            insertionPending: id =>
                useStore.getState().panels.animatedPanelInsert?.panel?.panelId === id,
            subscribe: listener => useStore.subscribe(listener),
            scheduleFrame: callback => requestAnimationFrame(callback),
            slide: slideTo,
        })
    }

    return (
        <div className={cn(styles.nav, styles.navBack)}>
            <button
                type="button"
                aria-label={`Back to ${label}`}
                title={`Back to ${label}`}
                data-testid="place-bar-back"
                className={styles.navButton}
                onClick={goBack}
            >
                <ArrowLeft size={24} strokeWidth={2.2} />
            </button>
        </div>
    )
}

function headPostLabel(quest: Quest | null | undefined) {
    const head = (quest as any)?.parent
    return (
        answerModel.getText(head)?.trim() ||
        (!head?.url?.tweet_id && head?.url?.title) ||
        head?.url?.address ||
        'Thread'
    )
}

type Channel = {
    teamId: string | null
    name: string
    filter: Filter | null
    // A music thread: the music channel, marked with a note.
    music?: boolean
}

// Which channel this panel lives in: the thread's stream, or the stream itself.
function useChannel(filter: Filter, quest: Quest | null): Channel {
    const q = quest as any
    const music = Boolean(q && questModel.isMusicChannelThread(q))
    const streamFilter = q ? questModel.channelFilter(q) : filter
    const teamId = q
        ? music
            ? String(gon.RADIO_TEAM_ID)
            : q.team_id
            ? String(q.team_id)
            : null
        : filter?.team_ids?.length === 1
        ? String(filter.team_ids[0])
        : null
    // A thread payload already carries its team; only a bare stream filter
    // may need the lookup to fetch it.
    const { team } = useTeamLookup(teamId, q?.team?.name ? q.team : null)
    const resolvedTeam: any = team || q?.team || null
    // A profile's name is its user's: without the user it reads "Loading...".
    const profileUserId = q
        ? undefined
        : filter?.user || filter?.user_ordinals || filter?.user_music
    const { user: profileUser } = useGetUser(profileUserId ? String(profileUserId) : undefined)
    const name = getStreamName(streamFilter, profileUser || null, true, resolvedTeam) || 'Stream'
    return { teamId, name, filter: streamFilter || null, music }
}

// The same scope icon the floating capsule carried (public, private, a
// channel's logo, notifications, ...), so a stream looks the same everywhere.
// Over a thread it opens the channel.
function ChannelLogo({
    channel,
    small = false,
    onOpen,
}: {
    channel: Channel
    small?: boolean
    onOpen?: () => void
}) {
    const { panel } = usePanelContext()
    const icon = <PanelScopeIcon panel={panel} filter={channel.filter} music={channel.music} />
    if (onOpen) {
        return (
            <button
                type="button"
                className={cn(styles.logo, styles.logoButton, small && styles.logoSmall)}
                onClick={onOpen}
                aria-label={`Open ${channel.name}`}
            >
                {icon}
            </button>
        )
    }
    return (
        <span className={cn(styles.logo, small && styles.logoSmall)} aria-hidden="true">
            {icon}
        </span>
    )
}

// Once a thread's title is in the header, the icon is its author's avatar.
// mailBadge: the other person in a direct message, marked with the letter's
// small mail badge on the avatar's corner.
function AuthorLogo({ user, mailBadge = false }: { user: any; mailBadge?: boolean }) {
    return (
        <span className={cn(styles.logo, styles.logoAvatar)} aria-hidden="true">
            <UserIcon user={user} contextStyles={styles.avatar} size="medium" />
            {mailBadge && (
                <span className={styles.mailBadge}>
                    <Mail size={9} strokeWidth={2.6} />
                </span>
            )}
        </span>
    )
}

// The channel crumb: for the owner of a root thread that can still be
// published, it opens the publish dropdown (who can see it, which channel it
// lives in), the control the thread's own crumb row used to carry, and wears
// a caret to say so; otherwise it opens the channel.
function ChannelCrumb({
    quest,
    name,
    parentQuestId,
    onOpen,
}: {
    quest: Quest
    name: string
    parentQuestId: string | null
    onOpen?: () => void
}) {
    const buttonRef = useRef<HTMLButtonElement>(null)
    const [menu, setMenu] = useState<{
        top: number
        left: number
        width: number
        maxHeight: number
    } | null>(null)
    const canPublish = canPublishFromQuestBreadcrumb({
        quest: quest as any,
        parentQuestId,
        isPublic: questModel.isPublic(quest as any),
        isBsv: perms.isPublic(quest as any),
        isOwner: perms.amQuestOwner(quest as any),
    })

    const onClick = () => {
        if (!canPublish) return onOpen?.()
        const rect = buttonRef.current?.getBoundingClientRect()
        if (!rect) return
        const pad = 12
        const width = Math.min(300, window.innerWidth - pad * 2)
        const top = Math.min(rect.bottom + 6, Math.max(pad, window.innerHeight - 220))
        setMenu({
            top,
            left: Math.min(Math.max(pad, rect.left), window.innerWidth - pad - width),
            width,
            // The space below the crumb, so the channel list scrolls to its end
            // instead of running past the bottom of the screen.
            maxHeight: Math.max(180, window.innerHeight - top - pad),
        })
    }

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className={cn(
                    styles.captionLink,
                    styles.channelCrumb,
                    canPublish && styles.channelCrumbMenu,
                    name.length <= 'Private'.length && styles.channelCrumbShort,
                )}
                onClick={onClick}
                disabled={!canPublish && !onOpen}
                aria-haspopup={canPublish ? 'menu' : undefined}
                aria-expanded={canPublish ? Boolean(menu) : undefined}
            >
                {canPublish ? (
                    <>
                        <span className={styles.channelCrumbName}>{name}</span>
                        <ChevronDown
                            size={12}
                            strokeWidth={2.4}
                            className={styles.channelCrumbCaret}
                            aria-hidden="true"
                        />
                    </>
                ) : (
                    name
                )}
            </button>
            {menu &&
                createPortal(
                    <div className={styles.menuBackdrop} onClick={() => setMenu(null)}>
                        <div
                            className={styles.menuPopover}
                            style={{
                                top: menu.top,
                                left: menu.left,
                                width: menu.width,
                                maxHeight: menu.maxHeight,
                            }}
                            onClick={ev => ev.stopPropagation()}
                        >
                            <PermsOptions
                                quest={quest}
                                close={() => setMenu(null)}
                                showHeader={true}
                            />
                        </div>
                    </div>,
                    document.body,
                )}
        </>
    )
}

function ThreadPlace({
    quest,
    channel,
    showTitle,
    showCrumbs = true,
}: {
    quest: Quest | null
    channel: Channel
    showTitle: boolean
    // False while the thread's own ancestor list is still on screen.
    showCrumbs?: boolean
}) {
    const { panel } = usePanelContext()
    const { openRight } = useOpenRight(panel?.panelId)

    if (!quest) return null

    if (isDirectMessageQuest(quest as any)) {
        return <DirectMessagePlace quest={quest} showTitle={showTitle} showCrumbs={showCrumbs} />
    }

    const streamFilter = questModel.channelFilter(quest)
    const parentQuestId = resolveQuestParentQuestId({
        questId: quest.id,
        parentQuestId: (quest as any).parent_quest_id,
        parentId: (quest as any).parent_id,
        parentAnswer: (quest as any).parent,
        getAnswerById: id => getCachedAnswer(String(id)),
    })
    const parentQuest = parentQuestId ? getCachedQuest(parentQuestId) : null
    // The posts above this thread, root first; without the full chain, the
    // parent thread's head post alone.
    const ancestors: any[] = Array.isArray((quest as any).ancestors)
        ? (quest as any).ancestors
        : parentQuest?.parent
        ? [parentQuest.parent]
        : []

    // Over the breadcrumb alone, the channel's icon sits just larger than its
    // name; with the title showing, the author's avatar takes its full size.
    const author = (quest as any).parent?.user
    const openChannel = streamFilter ? () => openRight(streamFilter) : undefined
    return (
        <>
            {showTitle && author ? (
                <AuthorLogo user={author} />
            ) : (
                <ChannelLogo channel={channel} small={!showTitle} onOpen={openChannel} />
            )}
            <div className={cn(styles.stack, !showTitle && styles.crumbOnly)}>
                <div className={styles.caption}>
                    <ChannelCrumb
                        quest={quest}
                        name={channel.name}
                        parentQuestId={parentQuestId}
                        onOpen={openChannel}
                    />
                    {(!parentQuestId || !showCrumbs) && <span className={styles.kind}>Thread</span>}
                    {parentQuestId && showCrumbs && (
                        <AncestorCrumbs
                            compact={isMobile}
                            className={styles.crumbsReveal}
                            ancestors={ancestors}
                            leadingSeparator
                            onOpen={i => {
                                const target = ancestorCrumbTarget(
                                    ancestors,
                                    i,
                                    parentQuestId,
                                    (quest as any).parent_id,
                                )
                                if (target) openRight(target as Filter)
                            }}
                        />
                    )}
                </div>
                {showTitle && <Title text={headPostLabel(quest)} />}
            </div>
        </>
    )
}

// A direct message's place is the other person: their avatar and name, as a
// channel's are for a channel thread. In the conversation the name opens their
// profile; in a branch it opens the conversation at the message the branch
// grew from, and the posts above the branch follow it as crumbs, minus the
// conversation's head (a system post the person stands in for). Someone
// invited into a branch has no side and nothing above it to see.
function DirectMessagePlace({
    quest,
    showTitle,
    showCrumbs,
}: {
    quest: Quest
    showTitle: boolean
    showCrumbs: boolean
}) {
    const { panel } = usePanelContext()
    const { openRight } = useOpenRight(panel?.panelId)
    const counterpartId = directMessageCounterpartId(quest as any, gon.currentUser?.id)
    const { user: fetchedCounterpart } = useGetUser(counterpartId || undefined, {
        enabled: !!counterpartId,
        refetchOnMount: false,
    })
    const counterpart: any = fetchedCounterpart || getCachedUser(counterpartId)
    const isConversation = isDirectMessageRoom(quest as any)
    // The conversation's own head is the system post: its header text is the
    // person, never that post.
    const title = showTitle && !isConversation ? <Title text={headPostLabel(quest)} /> : null

    if (!counterpartId) {
        return (
            <div className={cn(styles.stack, !showTitle && styles.crumbOnly)}>
                <div className={styles.caption}>Direct message</div>
                {title}
            </div>
        )
    }

    const ancestors: any[] = Array.isArray((quest as any).ancestors) ? (quest as any).ancestors : []
    const parentQuestId = resolveQuestParentQuestId({
        questId: quest.id,
        parentQuestId: (quest as any).parent_quest_id,
        parentId: (quest as any).parent_id,
        parentAnswer: (quest as any).parent,
        getAnswerById: id => getCachedAnswer(String(id)),
    })
    // Index 0 is the conversation's head when the chain reaches it.
    const skip = isDirectMessageConversationHead(ancestors[0], quest as any) ? 1 : 0
    const crumbs = ancestors.slice(skip)
    const openCrumb = (index: number) => {
        const target = ancestorCrumbTarget(
            ancestors,
            index,
            parentQuestId,
            (quest as any).parent_id,
        )
        if (target) openRight(target as Filter)
    }
    const openPerson = async () => {
        if (isConversation) return openRight(withDepth({ user: counterpartId }) as Filter)
        if (skip) return openCrumb(0)
        // No chain in the payload (it comes with scroll_ancestors): walk up to
        // the conversation the way the branch crumb does.
        const target = await resolveDirectMessageConversationTarget(quest as any, loadQuest)
        if (target) openRight(target as Filter)
    }
    const name = counterpart?.name || counterpart?.username || ''
    const author = (quest as any).parent?.user

    return (
        <>
            {title && author ? (
                <AuthorLogo user={author} />
            ) : (
                <AuthorLogo user={counterpart || { id: counterpartId }} mailBadge />
            )}
            <div className={cn(styles.stack, !title && styles.crumbOnly)}>
                <div className={styles.caption}>
                    <button
                        type="button"
                        className={cn(styles.captionLink, styles.channelCrumb)}
                        onClick={openPerson}
                        title={isConversation ? name : `Your conversation with ${name || 'them'}`}
                        data-testid="direct-message-place"
                    >
                        {name}
                    </button>
                    {!isConversation && showCrumbs && crumbs.length > 0 && (
                        <AncestorCrumbs
                            compact={isMobile}
                            className={styles.crumbsReveal}
                            ancestors={crumbs}
                            leadingSeparator
                            onOpen={i => openCrumb(i + skip)}
                        />
                    )}
                </div>
                {title}
            </div>
        </>
    )
}

// A conversation's card in a stream (Direct Messages) leads with the place the
// bar shows over the open conversation: who it's with. The card's own row is
// its latest message, which may be the viewer's. Its height is the stream
// estimate's crumb reserve (QUEST_STREAM_CRUMB_CHROME_PX), which it replaces.
export function DirectMessageCardHeader({
    quest,
    onOpen,
}: {
    quest: Quest
    onOpen?: (ev: React.MouseEvent) => void
}) {
    return (
        <div
            className={styles.directMessageCardHeader}
            data-testid="direct-message-card-header"
            onClick={ev => {
                // The name opens their profile; the rest opens the conversation.
                if ((ev.target as HTMLElement).closest('button')) return
                onOpen?.(ev)
            }}
        >
            <DirectMessagePlace quest={quest} showTitle={false} showCrumbs={false} />
        </div>
    )
}

// Team channels and the home scopes (Public, Following, Private) are the
// places people post into; feeds like notifications or history aren't.
function isChannelLike(channel: Channel) {
    const f: any = channel.filter
    return Boolean(channel.teamId || f?.public || f?.following || f?.private)
}

function StreamPlace({
    channel,
    visible,
    onHeadClick,
}: {
    channel: Channel
    visible: boolean
    onHeadClick?: () => void
}) {
    // The list's own switches (scope tabs, sort order), published by its
    // header card: pills with carets, so they read as view switches rather
    // than a path.
    const { panel } = usePanelContext()
    const view = usePanelViewControls(panel?.panelId)
    const tabs = view?.tabs && view.tabs.options.length > 1 ? view.tabs : null
    const order = view?.order && view.order.options.length > 1 ? view.order : null
    const tabsAsName = Boolean(tabs) && view?.title === 'Home'
    return (
        <div className={cn(styles.streamPlace, !visible && styles.hidden)} aria-hidden={!visible}>
            <ChannelLogo channel={channel} />
            {/* Home's scopes need no "Home": the current tab is the name, and
                it opens the switch to the others. Other tabbed lists keep their
                name with the tab as a pill beside it. */}
            {tabsAsName ? (
                <ViewPill control={tabs!} asTitle ariaLabel="Tab" />
            ) : (
                <div className={cn(styles.stack, styles.streamName)}>
                    <Title text={(tabs && view?.title) || channel.name} onClick={onHeadClick} />
                    {/* A top-level channel says what it is; a muted marker,
                        dropped once a breadcrumb names a place above, or a tab
                        takes over. */}
                    {!tabs && isChannelLike(channel) && (
                        <span className={styles.kind}>Channel</span>
                    )}
                </div>
            )}
            {tabs && !tabsAsName && <ViewPill control={tabs} accent ariaLabel="Tab" />}
            {order && <ViewPill control={order} className={styles.orderPill} ariaLabel="Sort" />}
        </div>
    )
}

// A thread's title scrolls to its head post; a stream's name does what the
// floating capsule did: refresh, or scroll to the top.
function Title({ text, onClick }: { text: string; onClick?: () => void }) {
    const handleClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
        if (onClick) onClick()
        else panelScroller(ev.currentTarget)?.scrollTo({ top: 0, behavior: 'smooth' })
    }
    return (
        <button type="button" className={styles.title} onClick={handleClick} title={text}>
            {text}
        </button>
    )
}

export default function PanelPlaceBar({
    filter,
    navigable,
    frame,
    onHeadClick,
    newItems,
}: {
    filter: Filter
    // Streams: refresh, or scroll to the top (the floating capsule's click).
    onHeadClick?: () => void
    // Streams: the "N New" chip, centred just under the header.
    newItems?: React.ReactNode
    // Only the focused panel carries back.
    navigable: boolean
    // Desktop: the bar is the header row of the panel's frame (the one-panel
    // column or a three-panel window), always drawn.
    frame?: 'column' | 'window'
}) {
    const framed = Boolean(frame)
    const { panelIndex, panel } = usePanelContext()
    // A new thread's editor: nothing above it yet (its channel is chosen in
    // the editor's own pill), so the header just names what this is.
    const isNewThread = Boolean((panel as any)?.newThread)
    const barRef = useRef<HTMLDivElement>(null)
    const questId = filter?.questId ? String(filter.questId) : null
    // cacheOnly: QuestContainer owns fetching; this just follows the same entry.
    const { quest: fetchedQuest } = useGetQuest(questId || undefined, { cacheOnly: true })
    const quest = ((questId && (fetchedQuest || getCachedQuest(questId))) || null) as Quest | null
    const channel = useChannel(filter, quest)
    // The one-panel column is centred, clear of the corner controls; phones and
    // three-panel windows sit under them.
    const cornerInsets = useCornerInsets(panelIndex, Boolean(questId))
    const insets = frame === 'column' ? { left: false, right: false } : cornerInsets
    const streamScrolledPast = useScrolledPast(barRef, STREAM_HEADER_CLEAR_PX)
    const headPostCovered = useHeadPostCovered(barRef, questId && Boolean(quest))
    const scrolledPast = questId ? headPostCovered : streamScrolledPast
    // A thread's header shows once the thread loads (always, in a desktop
    // frame). A channel's stays hidden while its own header card is on screen
    // and drops down once that card has scrolled away, so the two never name
    // the channel at the same time.
    // A direct message's conversation is a top-level place with its own letter
    // header: no way back, and no bar until the letter has scrolled away.
    const isLetter = !isNewThread && Boolean(quest) && isDirectMessageRoom(quest as any)
    const letterCovered = useLetterCovered(barRef, isLetter)
    const letterConcealed = isLetter && !letterCovered
    const banded = letterConcealed ? false : questId ? framed || Boolean(quest) : scrolledPast
    const concealed = (!questId && !scrolledPast) || letterConcealed
    const spineCovered = useAncestorSpineCovered(barRef, questId && Boolean(quest))
    useMobileChromeAutoHide(barRef, panelIndex, Boolean(questId) && headPostCovered)

    return (
        <div
            ref={barRef}
            data-testid="panel-place-bar"
            data-banded={banded ? 'true' : undefined}
            data-concealed={concealed ? 'true' : undefined}
            className={cn(
                styles.placeBar,
                framed && styles.framed,
                banded && styles.banded,
                concealed && styles.concealed,
                insets.left && styles.clearLeft,
                insets.right && styles.clearRight,
            )}
        >
            <span className={styles.band} aria-hidden="true" />
            {navigable && questId && !isNewThread && !isLetter && <PanelUp quest={quest} />}
            {letterConcealed ? null : isNewThread ? (
                <div className={styles.stack}>
                    <Title text="New Thread" />
                </div>
            ) : questId ? (
                <ThreadPlace
                    quest={quest}
                    channel={channel}
                    showTitle={scrolledPast}
                    showCrumbs={spineCovered}
                />
            ) : (
                <StreamPlace channel={channel} visible={scrolledPast} onHeadClick={onHeadClick} />
            )}
            {!questId && newItems && (
                <button
                    type="button"
                    className={styles.newItems}
                    aria-label="Show new posts"
                    onClick={onHeadClick}
                >
                    {newItems}
                </button>
            )}
        </div>
    )
}
